/* ============================================
   CLAPJOY GLOBAL JAVASCRIPT

   Scope note: the header, cart and mobile nav are stock Dawn. This file only
   drives the cj-* sections plus the video facade. Cart writes go through Dawn's
   <cart-drawer> so there is one cart UI and one source of truth for currency,
   discounts and errors.
   ============================================ */

(function () {
  'use strict';

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const REVEAL_SELECTOR = '.cj-reveal, .cj-reveal-left, .cj-reveal-right, .cj-reveal-scale, .cj-stagger';

  /* --- Scroll Reveal Animations ---
     Dawn sections use their own scroll-trigger system (animations.js), which
     re-inits on shopify:section:load. This one covers cj-* sections only. Do not
     put these classes on markup that the Section Rendering API replaces (e.g.
     the facet-filtered collection grid) -- replaced nodes are never observed and
     would stay at opacity 0. */
  function revealAll(root) {
    (root || document).querySelectorAll(REVEAL_SELECTOR).forEach((el) => el.classList.add('revealed'));
  }

  function initRevealAnimations(root) {
    const scope = root || document;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
    );

    scope.querySelectorAll(REVEAL_SELECTOR).forEach((el) => observer.observe(el));
  }

  /* --- Animated Counter --- */
  function animateCounter(el) {
    const target = parseInt(el.dataset.cjCounter, 10);
    if (isNaN(target)) return;

    const suffix = el.dataset.cjSuffix || '';
    const duration = 2000;
    const start = performance.now();

    function tick(now) {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = Math.round(target * eased);
      el.textContent = current.toLocaleString('en-IN') + suffix;
      if (progress < 1) requestAnimationFrame(tick);
    }

    requestAnimationFrame(tick);
  }

  function setCountersFinal(root) {
    (root || document).querySelectorAll('[data-cj-counter]').forEach((el) => {
      const target = parseInt(el.dataset.cjCounter, 10);
      if (isNaN(target)) return;
      el.textContent = target.toLocaleString('en-IN') + (el.dataset.cjSuffix || '');
    });
  }

  function initCounters(root) {
    const counters = (root || document).querySelectorAll('[data-cj-counter]');
    if (!counters.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          animateCounter(entry.target);
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.5 }
    );

    counters.forEach((el) => observer.observe(el));
  }

  /* --- Video facade ---
     Delegated from document so it keeps working through Section Rendering and
     in the theme editor. */
  function initVideoFacade() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-cj-video="youtube"]');
      if (!btn) return;

      const id = btn.dataset.cjVideoId;
      if (!id) return;

      const iframe = document.createElement('iframe');
      iframe.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}?autoplay=1&rel=0&playsinline=1`;
      iframe.allow = 'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; web-share';
      iframe.allowFullscreen = true;
      iframe.title = btn.getAttribute('aria-label') || 'Video';
      iframe.loading = 'lazy';

      // Replacing the button in place keeps the frame's box, so no layout shift.
      btn.replaceWith(iframe);
      iframe.focus();
    });
  }

  /* --- Add to cart (cj product cards) ---
     Routes through Dawn's <cart-drawer> rather than a bespoke drawer: Dawn
     already handles money formatting per market, discounts, cart notes, line
     item errors and focus trapping. */
  function initAddToCart() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-cj-add-to-cart]');
      if (!btn) return;

      e.preventDefault();

      // data-cj-add-to-cart is a bare marker; the id lives in data-variant-id.
      const variantId = btn.dataset.variantId;
      if (!variantId || btn.hasAttribute('aria-disabled')) return;

      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

      btn.setAttribute('aria-disabled', 'true');
      btn.classList.add('loading');

      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', 1);

      if (cart) {
        formData.append('sections', cart.getSectionsToRender().map((s) => s.id));
        formData.append('sections_url', window.location.pathname);
        cart.setActiveElement(document.activeElement);
      }

      fetch(`${window.routes.cart_add_url}`, {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: formData,
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.status) {
            if (typeof publish === 'function') {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'cj-product-card',
                productVariantId: variantId,
                errors: data.errors || data.description,
                message: data.message,
              });
            }
            console.error('[clapjoy] add to cart failed:', data.description || data.message);
            return;
          }

          if (typeof publish === 'function') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'cj-product-card',
              productVariantId: variantId,
              cartData: data,
            });
          }

          if (cart) {
            cart.renderContents(data);
          } else {
            window.location = window.routes.cart_url;
          }
        })
        .catch((err) => console.error('[clapjoy] add to cart failed:', err))
        .finally(() => {
          btn.removeAttribute('aria-disabled');
          btn.classList.remove('loading');
        });
    });
  }

  /* --- Sticky add to cart (product page) --- */
  function initStickyATC() {
    const stickyBar = document.querySelector('.cj-sticky-atc');
    const mainATC = document.querySelector('[data-cj-main-atc]');

    if (!stickyBar || !mainATC) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          stickyBar.classList.toggle('visible', !entry.isIntersecting);
        });
      },
      { threshold: 0 }
    );

    observer.observe(mainATC);

    // Hand off to the real form rather than duplicating the add-to-cart path.
    const stickyBtn = stickyBar.querySelector('[data-cj-sticky-atc-btn]');
    if (stickyBtn) {
      stickyBtn.addEventListener('click', () => mainATC.click());
    }
  }

  /* --- Slider / Carousel Navigation --- */
  function initSliders(root) {
    (root || document).querySelectorAll('[data-cj-slider]').forEach((slider) => {
      const track = slider.querySelector('[data-cj-slider-track]');
      const prevBtn = slider.querySelector('[data-cj-slider-prev]');
      const nextBtn = slider.querySelector('[data-cj-slider-next]');

      if (!track) return;

      const scrollAmount = () => {
        const child = track.children[0];
        return child ? child.offsetWidth + parseInt(getComputedStyle(track).gap || '12', 10) : 300;
      };

      if (prevBtn) {
        prevBtn.addEventListener('click', () => {
          track.scrollBy({ left: -scrollAmount(), behavior: 'smooth' });
        });
      }

      if (nextBtn) {
        nextBtn.addEventListener('click', () => {
          track.scrollBy({ left: scrollAmount(), behavior: 'smooth' });
        });
      }
    });
  }

  /* --- FAQ accordion --- */
  function initAccordions(root) {
    (root || document).querySelectorAll('.cj-faq__item').forEach((item) => {
      const question = item.querySelector('.cj-faq__question');
      if (!question || question.dataset.cjBound) return;
      question.dataset.cjBound = 'true';

      question.addEventListener('click', (e) => {
        // <summary> toggles <details> itself. Without this the browser's default
        // action runs after the handler and undoes the toggle below.
        e.preventDefault();

        const isOpen = item.hasAttribute('open');

        const parent = item.closest('.cj-faq__list');
        if (parent) {
          parent.querySelectorAll('.cj-faq__item[open]').forEach((other) => {
            if (other !== item) other.removeAttribute('open');
          });
        }

        if (isOpen) {
          item.removeAttribute('open');
        } else {
          item.setAttribute('open', '');
        }
      });
    });
  }

  /* --- Marquee pause on hover --- */
  function initMarquee(root) {
    const marquee = (root || document).querySelector('.cj-announcement__track');
    if (!marquee) return;
    const parent = marquee.parentElement;

    parent.addEventListener('mouseenter', () => {
      marquee.style.animationPlayState = 'paused';
    });
    parent.addEventListener('mouseleave', () => {
      marquee.style.animationPlayState = 'running';
    });
  }

  /* --- Per-section init, re-runnable for the theme editor --- */
  function initSection(root) {
    if (prefersReducedMotion) {
      revealAll(root);
      setCountersFinal(root);
    } else {
      initRevealAnimations(root);
      initCounters(root);
    }

    initSliders(root);
    initAccordions(root);
    initMarquee(root);
    initStickyATC();
  }

  /* --- Document-level init, bound once --- */
  function init() {
    initVideoFacade();
    initAddToCart();
    initSection(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Sections added or re-rendered in the theme editor need their own pass.
  document.addEventListener('shopify:section:load', (e) => initSection(e.target));
})();
